import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Editor } from "@tinymce/tinymce-react";
import type { Editor as TinyMceEditorInstance } from "tinymce";
import { Button } from "@mui/material";
import { IDocumentTemplateData } from "@/types/DocumentTemplateData";
import { useTranslation } from "react-i18next";
import styles from "@/components/tinymce/tinymce.module.css";

interface Props {
  value?: IDocumentTemplateData | any;
  handleOnSave?: (content: any) => void;
  lastSaved?: any;
  onChange?: (content: any) => void;
  onBlur?: () => void;
  showFooter?: boolean;
  height?: number;
  disabled?: boolean;
}

export interface TinyMceEditorHandle {
  insertContent: (content: string) => void;
}

const TinyMceEditor = forwardRef<TinyMceEditorHandle, Props>(
  (
    {
      value,
      handleOnSave,
      lastSaved,
      onChange,
      onBlur,
      showFooter = true,
      height = 500,
      disabled = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [localValue, setLocalValue] = useState<any>(value);
    const editorRef = useRef<TinyMceEditorInstance | null>(null);
    const hasRegisteredCursorRef = useRef(false);

    useImperativeHandle(ref, () => ({
      insertContent: (content: string) => {
        if (editorRef.current) {
          const hasRegisteredCursor = hasRegisteredCursorRef.current;
          editorRef.current.focus();

          if (!hasRegisteredCursor) {
            editorRef.current.selection.select(editorRef.current.getBody(), true);
            editorRef.current.selection.collapse(false);
          }

          editorRef.current.insertContent(content);
        }
      },
    }));

    useEffect(() => {
      if (value !== localValue) {
        setLocalValue(value);
      }
    }, [value]);
    const onChangeHandler = (content: any) => {
      setLocalValue(content);
      if (onChange) {
        onChange(content);
      }
    };

    const onSavehandler = () => {
      const content = editorRef.current?.getContent();
      if (handleOnSave && content !== undefined) {
        handleOnSave(content);
      }
    };

    return (
      <>
        <Editor
          licenseKey="gpl"
          onEditorChange={onChangeHandler}
          disabled={disabled}
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          onInit={(evt, editor) => {
            editorRef.current = editor;
            editor.setContent(localValue);
          }}
          onFocus={() => {
            hasRegisteredCursorRef.current = true;
          }}
          onBlur={onBlur}
          value={localValue}
          init={{
            height,
            menubar: false,
            plugins: [
              "advlist",
              "autolink",
              "lists",
              "link",
              "image",
              "charmap",
              "anchor",
              "searchreplace",
              "visualblocks",
              "code",
              "fullscreen",
              "insertdatetime",
              "media",
              "table",
              "preview",
              "help",
              "wordcount",
              "pagebreak",
            ],
            paste_as_text: true,
            remove_trailing_brs: false,
            pagebreak_separator: '<div style="break-after:page"></div>',
            toolbar:
              "undo redo | blocks | " +
              "bold italic forecolor | alignleft aligncenter " +
              "alignright alignjustify | bullist numlist outdent indent | " +
              "removeformat | hr pagebreak | image editimage | link | preview | table | code | help ",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
          }}
        />
        {showFooter && (
          <div className={styles.footerContainer}>
            <Button
              color="secondary"
              variant="contained"
              type="button"
              onClick={onSavehandler}
              style={{ fontSize: "inherit" }}
            >
              Speichern
            </Button>
            {lastSaved && typeof (lastSaved === "string") && (
              <div className={styles.lastSaved}>
                {t("last_saved")}: <span>{lastSaved}</span>
              </div>
            )}
          </div>
        )}
      </>
    );
  },
);

TinyMceEditor.displayName = "TinyMceEditor";

export default TinyMceEditor;
